<script setup>
  import { defineProps, ref, onMounted, computed } from "vue";
  import SearchComponent from "./SearchComponent.vue";
  import axios from "axios";
  import FieldSort from "./FieldSort.vue";
  import { debounce } from "lodash";
  import "animate.css";
  import CanvasComponent from "./canvasComponent.vue";

  const ANIMATION_DURATION = 350;
  const DEBOUNCE_DELAY = 500;
  const isLoading = ref(true);
  const trans = (key) => {
    return _.get(window.translations, key, key);
  };
  const props = defineProps({
    breadcrumb: Array,
    model: Object,
    productField: Boolean
  });

  const currentProducts = ref(props.model?.paginatedProducts?.products || []);
  const categorizedProducts = computed(() => {
    const standardProducts = [];
    const otherProducts = [];
    const TARGET_CATEGORY = "Standard Sizes and Designing Components";

    const products = currentProducts.value;

    if (products && Array.isArray(products)) {
      products.forEach(product => {
        if (product.majorCategory === TARGET_CATEGORY) {
          standardProducts.push(product);
        } else if (product.majorCategory) {
          otherProducts.push(product);
        }
      });
    }
    return {
      standard: standardProducts,
      other: otherProducts
    };
  });

  const error = ref(null);
  const searchQuery = ref("");
  const productsLayout = ref("grid-6-columns");
  const pagination = ref({
    current_page: 1,
    per_page: 100,
    total: 0
  });
  const currentSearchTerm = ref("");
  const isLayoutSwitching = ref(false);

  const handleUpdateQuery = (queryString, newTerm) => {
    const url = new URL(window.location.href);
    url.searchParams.delete("q");
    url.searchParams.delete("name");

    if (newTerm) {
      url.searchParams.set(queryString, newTerm);
    }
    window.history.replaceState(null, "", url.toString());

    currentSearchTerm.value = newTerm;
    isLoading.value = true;
    debouncedFetchProducts();
  };

  const loadQueryStringFromUrl = () => {
    const url = new URL(window.location);
    if (url.searchParams.has("q")) {
      searchQuery.value = url.searchParams.get("q");
      currentSearchTerm.value = url.searchParams.get("q");
    } else {
      searchQuery.value = "";
      currentSearchTerm.value = "";
    }
    if (url.searchParams.has("perRow")) {
      const perRow = parseInt(url.searchParams.get("perRow"));
      productsLayout.value = perRow === 6 ? "grid-6-columns" : "grid-3-columns";
    }
  };

  const handleLayoutChange = (newLayout) => {
    isLayoutSwitching.value = true;

    setTimeout(() => {
      productsLayout.value = newLayout;

      const url = new URL(window.location);
      let perRowValue;
      if (newLayout === "grid-3-columns") {
        perRowValue = 3;
      } else if (newLayout === "grid-6-columns") {
        perRowValue = 6;
      } else {
        perRowValue = 6;
      }
      url.searchParams.set("perRow", perRowValue);
      window.history.replaceState(null, "", url.toString());

      isLayoutSwitching.value = false;

    }, ANIMATION_DURATION);
  };

  const getGroups = (product) => {
    const svgContent = product.featured_image?.svg || "";

    if (!svgContent) {
      return [];
    }
    const svg = new DOMParser().parseFromString(svgContent, "image/svg+xml");
    const groups = svg.querySelectorAll("g[id]");
    const returnGroups = [];

    const getColorImage = (id) => {
      return product.colors.find((x) => x.id === id)?.featured_image_300x300;
    };
    if (groups.length === 2) {
      returnGroups.push({
        groupId: groups[0].id,
        imageUrl: getColorImage(18)
      });
      returnGroups.push({
        groupId: groups[1].id,
        imageUrl: getColorImage(16)
      });
    } else if (groups.length === 1) {

      returnGroups.push({
        groupId: groups[0].id,
        imageUrl: getColorImage(3)
      });
    }

    return returnGroups;
  };

  const fetchProducts = async () => {
    const requestData = {
      url: window.location.pathname,
      page: pagination.value.current_page,
      perPage: pagination.value.per_page
    };

    if (currentSearchTerm.value) {
      requestData.q = currentSearchTerm.value;
    }

    const url = new URL(window.location.href);
    if (url.searchParams.has("name")) {
      requestData.name = url.searchParams.get("name");
    }
    console.log("-> Realizando busca de produtos com os seguintes dados:", requestData);
    try {
      const csrfToken = document.head.querySelector("meta[name=\"csrf-token\"]").content;
      const { data } = await axios.post(`/integrations/products_topcer`, requestData, {
        headers: {
          "X-CSRF-TOKEN": csrfToken
        }
      });
      currentProducts.value = data.products || [];
      if (data.pagination) {
        pagination.value = data.pagination;
      }

    } catch (err) {
      error.value = "Falha ao buscar os produtos: " + err.message;
      console.error(error.value);
    } finally {
      isLoading.value = false;
    }
  };

  const debouncedFetchProducts = debounce(fetchProducts, DEBOUNCE_DELAY);

  onMounted(() => {
    loadQueryStringFromUrl();
    fetchProducts();
  });

  const calcSize = (product, size) => {
    const rawSvgSize = product.svgSize || "80%";
    const svgSizeString = String(rawSvgSize);
    const svgSize = parseInt(svgSizeString.replace("%", ""), 10);
    const finalSvgSize = isNaN(svgSize) ? 80 : svgSize;
    return ((finalSvgSize / 80)) * size;
  };
</script>

<template>
  <div class="container-geral-field-materials">
    <Transition name="fade-overlay">
      <div v-if="isLoading" id="loading-overlay">
        <div class="spinner"></div>
      </div>
    </Transition>
    <div class="container-breadcrumb">
      <nav aria-label="breadcrumb">
        <ol class="breadcrumb">
          <li
            v-for="(item, index) in breadcrumb"
            :key="index"
            class="breadcrumb-item"
            :class="{ active: index === breadcrumb.length - 1 }"
            :aria-current="index === breadcrumb.length - 1 ? 'page' : null">
            <a v-if="index < breadcrumb.length - 1" :href="item.url">
              Products /{{ item.label }}
            </a>
            <span v-else>Products /{{ item.label }}</span>
          </li>
        </ol>
      </nav>
    </div>
    <div class="container-search">
      <search-component
        @update:searchTerm="term => handleUpdateQuery('q', term)"
      ></search-component>
    </div>
    <div class="content-filters">
      <div class="content-sort">
        <field-sort
          :selected-layout="productsLayout"
          @update:layout="handleLayoutChange"
        ></field-sort>
      </div>
    </div>
    <div
      class="content-cards"
      v-if="productField">
      <div>
        <h3>Standard Sizes and Designing Components</h3>
        <div v-if="!isLayoutSwitching" :class="productsLayout">
          <template v-if="productsLayout === 'grid-6-columns'">
            <div
              v-for="product in currentProducts" :key="product.id"
              class="div-product">
              <a :href="product.slug">
                <div class="card-product">
                  <div class="zoom">
                    <div v-if="!product.variationProducts[0].featured_image.isSvg" class="image-content">
                      <img :src="product.variationProducts[0].featured_image.mediaCollection['thumbnail_field_materials']"
                           :alt="product.variationProducts[0].title">
                    </div>
                    <div v-else>
                      <CanvasComponent
                        :svg-content="product.featured_image?.svg"
                        :group-images="getGroups(product)"
                        :width="calcSize(product, 150)"
                        :height="calcSize(product, 150)"
                        :pixel-ratio="2"
                      />
                    </div>

                  </div>
                </div>
                <div class="body-card">
                  <p>{{ product.label_list }}</p>
                  <p>{{ product.packing_dimensions }}</p>
                </div>
              </a>
            </div>
          </template>
          <template v-else-if="productsLayout === 'grid-3-columns'">
            <div v-for="product in currentProducts" :key="product.id" class="card-pair">
              <div
                :key="product.id"
                class="product-wrapper">
                <a :href="product.slug">
                  <div class="card-product">
                    <div class="zoom" v-if="productField">
                      <div v-if="!product.variationProducts[0].featured_image.isSvg" class="image-content">
                        <img :src="product.variationProducts[0].featured_image.mediaCollection['thumbnail_field_materials']"
                             :alt="product.variationProducts[0].title">
                      </div>
                      <div v-else>
                        <CanvasComponent
                          :svg-content="product.featured_image?.svg"
                          :group-images="getGroups(product)"
                          :width="calcSize(product, 70)"
                          :height="calcSize(product, 70)"
                          :pixel-ratio="1"
                        />
                      </div>
                    </div>
                    <div class="zoom" v-else>
                      <img :src="product.featured_image.fullUrl" :alt="product.name" />
                    </div>
                  </div>
                  <div class="body-card">
                    <p>{{ product.name }}</p>
                    <p>{{ product.packing_dimensions }}</p>
                  </div>
                </a>
              </div>
            </div>
          </template>
        </div>
        <p v-if="currentProducts.length === 0 && !isLoading">
          {{ trans("No results were found for your search in this group.") }}.</p>
      </div>
    </div>
    <div v-else>
      <div v-if="categorizedProducts.standard.length > 0">
        <div
          class="content-cards">
          <div>
            <h3>Standard Sizes and Designing Components</h3>
            <div v-if="!isLayoutSwitching" :class="productsLayout">
              <template v-if="productsLayout === 'grid-6-columns'">
                <div
                  v-for="product in categorizedProducts.standard" :key="product.id || product.slug"
                  class="div-product">
                  <a :href="product.slug">
                    <div class="card-product">
                      <div class="zoom"
                           :class="{
                             'class-25-percent': product.svgSize === '25%',
                             'class-50-percent': product.svgSize === '50%',
                             'class-65-percent': product.svgSize === '65%',
                             'class-80-percent': !product.svgSize || (product.svgSize !== '25%'
                              && product.svgSize !== '50%'
                              && product.svgSize !== '65%')}">
                        <img :src="product.featured_image.fullUrl" alt="">
                      </div>
                    </div>
                    <div class="body-card">
                      <p>{{ product.label_list }}</p>
                      <p>{{ product.packing_dimensions }}</p>
                    </div>
                  </a>
                </div>
              </template>
              <template v-else-if="productsLayout === 'grid-3-columns'">
                <div v-for="product in categorizedProducts.standard" :key="product.id || product.slug"
                     class="card-pair">
                  <div
                    class="product-wrapper">
                    <a :href="product.slug">
                      <div class="card-product">
                        <div class="zoom"
                             :class="{
                             'class-25-percent': product.svgSize === '25%',
                             'class-50-percent': product.svgSize === '50%',
                             'class-65-percent': product.svgSize === '65%',
                             'class-80-percent': !product.svgSize || (product.svgSize !== '25%'
                              && product.svgSize !== '50%'
                              && product.svgSize !== '65%')}">
                          <img :src="product.featured_image.fullUrl" alt="">
                        </div>
                      </div>
                      <div class="body-card">
                        <p>{{ product.label_list }}</p>
                        <p>{{ product.packing_dimensions }}</p>
                      </div>
                    </a>
                  </div>
                </div>
              </template>
            </div>
            <p v-if="currentProducts.length === 0 && !isLoading">
              {{ trans("No results were found for your search in this group.") }}</p>
          </div>
        </div>
      </div>
      <div v-if="categorizedProducts.other.length > 0">
        <div
          class="content-cards">
          <div>
            <h3>Other Designing Components</h3>
            <div v-if="!isLayoutSwitching" :class="productsLayout">
              <template v-if="productsLayout === 'grid-6-columns'">
                <div
                  v-for="product in categorizedProducts.other" :key="product.id || product.slug"
                  class="div-product">
                  <a :href="product.slug">
                    <div class="card-product">
                      <div class="zoom"
                           :class="{
                             'class-25-percent': product.svgSize === '25%',
                             'class-50-percent': product.svgSize === '50%',
                             'class-65-percent': product.svgSize === '65%',
                             'class-80-percent': !product.svgSize || (product.svgSize !== '25%'
                             && product.svgSize !== '50%'
                             && product.svgSize !== '65%')}">
                        <img :src="product.featured_image.fullUrl" alt="">
                      </div>
                    </div>
                    <div class="body-card">
                      <p>{{ product.label_list }}</p>
                      <p>{{ product.packing_dimensions }}</p>
                    </div>
                  </a>
                </div>
              </template>
              <template v-else-if="productsLayout === 'grid-3-columns'">
                <div v-for="product in categorizedProducts.other" :key="product.id || product.slug"
                     class="card-pair">
                  <div
                    :key="product.id"
                    class="product-wrapper">
                    <a :href="product.slug">
                      <div class="card-product">
                        <div class="zoom" v-if="productField">
                          <CanvasComponent
                            :svg-content="product.featured_image?.svg"
                            :group-images="getGroups(product)"
                            :width="70"
                            :height="70"
                            :pixel-ratio="1"
                          />
                        </div>
                        <div class="zoom" v-else>
                          <img :src="product.featured_image.fullUrl" :alt="product.name" />
                        </div>
                      </div>
                      <div class="body-card">
                        <p>{{ product.label_list }}</p>
                        <p>{{ product.packing_dimensions }}</p>
                      </div>
                    </a>
                  </div>
                </div>
              </template>
            </div>
            <p v-if="currentProducts.length === 0 && !isLoading">
              {{ trans("No results were found for your search in this group.") }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
</style>
